import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ar from "./ar";
import en from "./en";
import { ApiError } from "@/shared/api/client";
import { fieldMessageAr } from "./fieldMessages";

type Dict = Record<string, unknown>;

/** A module's own strings, merged over the shared ones at startup. */
export interface Strings { ar: Dict; en: Dict }

function merge(a: Dict, b: Dict): Dict {
  const out: Dict = { ...a };
  for (const [k, v] of Object.entries(b)) {
    out[k] = v && typeof v === "object" && !Array.isArray(v) && a[k] && typeof a[k] === "object" ? merge(a[k] as Dict, v as Dict) : v;
  }
  return out;
}

const KEY = "clientos.lang";
const isLang = (l: unknown): l is "ar" | "en" => l === "ar" || l === "en";
// The language this device was given: by a link (the English website's sign-up: ?lang=en), else
// the one last picked here. Without one, pages follow the workspace's language, then the person's.
const asked = typeof location === "undefined" ? null : new URLSearchParams(location.search).get("lang");
let picked = isLang(asked) ? asked : (() => {
  try {
    const l = localStorage.getItem(KEY);
    return isLang(l) ? l : null;
  } catch {
    return null;
  }
})();

function keep(lng: string) {
  try {
    localStorage.setItem(KEY, lng);
  } catch {
    /* private mode */
  }
}

/** Starts i18n with the shared strings plus every module's. Call once, before rendering. */
export function initI18n(modules: Strings[]) {
  const all = (base: Dict, lang: keyof Strings) => modules.reduce((acc, m) => merge(acc, m[lang]), base);
  void i18n.use(initReactI18next).init({
    resources: { ar: { translation: all(ar, "ar") }, en: { translation: all(en, "en") } },
    lng: picked ?? "ar",
    fallbackLng: "ar",
    interpolation: { escapeValue: false },
    returnObjects: true,
  });
  applyDir(i18n.language);
  if (isLang(asked)) keep(asked);
}

function applyDir(lng: string) {
  document.documentElement.lang = lng;
  document.documentElement.dir = lng === "ar" ? "rtl" : "ltr";
}
i18n.on("languageChanged", applyDir);

/** The person picks a language: shown at once and kept on this device, over any it would follow. */
export function pickLanguage(lng: string) {
  if (!isLang(lng)) return;
  picked = lng;
  keep(lng);
  void i18n.changeLanguage(lng);
}

/**
 * Shows the workspace's language (on its sign-in pages) or the signed-in person's own, unless
 * this device was given one: a new device, a new address after signing up, the demo company.
 */
export function followLanguage(lng: string | undefined) {
  if (!picked && isLang(lng) && lng !== i18n.language) void i18n.changeLanguage(lng);
}

/** A record's name in the language shown: its English name in English, when it has one. */
export function localName(x: { name_ar: string; name_en?: string | null }): string {
  return (i18n.language === "en" && x.name_en) || x.name_ar;
}

/** A company's legal name in the language shown. */
export function companyName(c?: { legal_name_ar: string; legal_name_en?: string | null } | null): string {
  return c ? (i18n.language === "en" && c.legal_name_en) || c.legal_name_ar : "";
}

/** Items one after another in the language shown: "a، b" in Arabic, "a, b" in English. Empty ones are left out. */
export function joinList(items: readonly (string | null | undefined)[]): string {
  return items.filter(Boolean).join(i18n.language === "en" ? ", " : "، ");
}

// Error keys contain dots (API codes), so look them up directly instead of via t().
function errorText(code: string): string | undefined {
  const bundle = i18n.getResourceBundle(i18n.language, "translation") as { errors: Record<string, string> };
  return bundle?.errors?.[code];
}

/** What the API said about a field, in the user's language. */
export function fieldMessage(message: string): string {
  if (i18n.language !== "ar") return message;
  return fieldMessageAr(message) ?? errorText("field.invalid") ?? message;
}

/**
 * Human message for any error: API problem codes are translated, detail is the fallback.
 * A form the API found a field wrong in says what's wrong with it, not just that it is.
 */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.fields.length > 0) return fieldMessage(err.fields[0].message);
    return errorText(err.code) ?? (err.detail || errorText("server.error") || err.code);
  }
  if (err instanceof TypeError) return errorText("network") ?? "Network error";
  return errorText("server.error") ?? "Error";
}

export default i18n;

/** An API error code in the user's language, when there's a translation for it. */
export function codeMessage(code: string): string | undefined {
  return errorText(code);
}
